import { useEffect, useMemo, useRef, useState } from 'react';
import './OutletSearch.css';

/**
 * Type-to-search outlet picker. Matches name, outlet code and city.
 * outlets: the outlets to choose from; value: selected outlet id ('' = none);
 * describe(outlet): label text; onChange(outlet | null).
 */
function OutletSearch({ outlets, value, describe, onChange, placeholder = 'Type outlet name, code or city…' }) {
  const selected = useMemo(
    () => outlets.find((outlet) => String(outlet.id) === String(value)) || null,
    [outlets, value],
  );
  const [text, setText] = useState(selected ? describe(selected) : '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const listId = useRef(`outlet-list-${Math.random().toString(36).slice(2, 8)}`).current;

  // Show the chosen outlet's name when the selection changes from outside.
  useEffect(() => {
    setText(selected ? describe(selected) : '');
    // describe is a plain helper; re-run only when the selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
        setText(selected ? describe(selected) : '');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const matches = useMemo(() => {
    const term = text.trim().toLowerCase();
    // While the box shows the selected outlet's own name, list everything.
    if (!term || (selected && text === describe(selected))) return outlets.slice(0, 200);
    const words = term.split(/\s+/);
    return outlets
      .filter((outlet) => {
        const hay = `${outlet.name || ''} ${outlet.outlet_code || ''} ${outlet.city || ''} ${outlet.address || ''}`.toLowerCase();
        return words.every((word) => hay.includes(word));
      })
      .slice(0, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets, text, selected]);

  const choose = (outlet) => {
    onChange(outlet);
    setText(describe(outlet));
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && matches[active]) {
        event.preventDefault();
        choose(matches[active]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setText(selected ? describe(selected) : '');
    }
  };

  return (
    <div className="outlet-search" ref={boxRef}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={text}
        placeholder={placeholder}
        onFocus={(event) => {
          setOpen(true);
          setActive(0);
          event.target.select();
        }}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
          setActive(0);
          if (!event.target.value.trim() && selected) onChange(null);
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <ul className="outlet-search__list" id={listId} role="listbox">
          {matches.length === 0 ? (
            <li className="outlet-search__empty">No outlet matches “{text.trim()}”.</li>
          ) : (
            matches.map((outlet, index) => (
              <li
                key={outlet.id}
                role="option"
                aria-selected={String(outlet.id) === String(value)}
                className={`outlet-search__option${index === active ? ' outlet-search__option--active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(outlet);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <strong>{outlet.name}</strong>
                <span>
                  {[outlet.outlet_code, outlet.city].filter(Boolean).join(' · ') || '—'}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export default OutletSearch;
